import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import Loading from 'views/MLoading/MLoading';
import MToast from 'views/MToast/MToast';
import RouteConfig from 'components/RouteConfig/RouteConfig';

import classes from './App.module.scss';
import { AppRouteType, IAppRoute } from './App.model';
import { MToastProps } from 'views/MToast/MToast.model';
import PendingParametersWrapper from 'views/PendingParametersWrapper/PendingParametersWrapper';

type Props = {
  loadingSpinner: number | boolean;
  isAuthenticated: boolean;
  routesList: IAppRoute[];
  toast: MToastProps | null;
  showToast: boolean;
  onHideToast: () => void;
  onToastShown: () => void;
};

const AppView: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  const generateRoutes = (routes: IAppRoute[]) => {
    return routes.map((route: IAppRoute) => {
      const Component: React.FC | undefined = route['component'];
      const jsx: JSX.Element = route['jsx'];

      return (
        <Route
          key={route.path}
          path={route.path}
          element={
            route.type === AppRouteType.Fallback ? (
              // 'replace' keeps a blocked page out of the history, so the back button doesn't return to it
              <Navigate to={route.to} replace></Navigate>
            ) : (
              <React.Fragment>
                <RouteConfig pageName={route.pageName}></RouteConfig>
                {route.shouldWaitForAppParameters ? (
                  <PendingParametersWrapper>{Component ? <Component /> : jsx}</PendingParametersWrapper>
                ) : Component ? (
                  <Component />
                ) : (
                  jsx
                )}
              </React.Fragment>
            )
          }
        >
          {route.children ? generateRoutes(route.children) : null}
        </Route>
      );
    });
  };

  return (
    <React.Fragment>
      <Loading
        show={props.loadingSpinner !== false}
        percent={typeof props.loadingSpinner === 'number' ? props.loadingSpinner : null}
      ></Loading>
      <React.Suspense
        fallback={
          <div className={classes['loading']}>
            <div className="sk-spinner sk-spinner-pulse"></div>
          </div>
        }
      >
        <Routes>{generateRoutes(props.routesList)}</Routes>

        <CSSTransition
          in={props.showToast}
          timeout={{ exit: 5000 }}
          classNames={{
            exitActive: classes['toast--close'],
          }}
          unmountOnExit
          onEntered={props.onToastShown}
          onExited={props.onHideToast}
        >
          <MToast className={classes['toast']} proggressBarClassName={classes['toastProggressBar']} {...props.toast}></MToast>
        </CSSTransition>
      </React.Suspense>
    </React.Fragment>
  );
};

AppView.displayName = 'AppView';
AppView.defaultProps = {};

export default AppView;
